import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '../components/ui/sheet';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Our Process', path: '/process' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-navy/95 backdrop-blur-sm shadow-lg' 
          : 'bg-navy'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center space-x-2 group"
          >
            <span className="text-2xl font-playfair font-bold text-white tracking-tight">
              C.A.B
            </span>
            <span className="text-sm font-inter text-tan tracking-widest uppercase">
              Enterprises
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-inter font-medium tracking-wide transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-copper'
                    : 'text-white/80 hover:text-copper'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="tel:+19453584803" 
              className="flex items-center space-x-2 text-white/80 hover:text-copper transition-colors"
              data-testid="phone-link"
            >
              <Phone size={16} />
              <span className="text-sm font-inter">(945) 358-4803</span>
            </a>
            <Link to="/contact">
              <Button 
                data-testid="get-estimate-btn"
                className="bg-copper hover:bg-copper/90 text-white px-6 py-2 text-xs font-bold uppercase tracking-widest"
              >
                Book Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button 
                variant="ghost" 
                size="icon" 
                data-testid="mobile-menu-btn"
                className="text-white hover:bg-white/10"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </Button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="bg-navy border-navy w-full sm:w-80"
              data-testid="mobile-menu"
              aria-describedby="mobile-menu-description"
            >
              <VisuallyHidden.Root>
                <SheetTitle>Navigation Menu</SheetTitle>
                <SheetDescription id="mobile-menu-description">
                  Main navigation menu for C.A.B Enterprises
                </SheetDescription>
              </VisuallyHidden.Root>
              <div className="flex flex-col h-full pt-8">
                <nav className="flex flex-col space-y-6">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                      className={`text-lg font-playfair transition-colors ${
                        isActive(link.path)
                          ? 'text-copper'
                          : 'text-white/80 hover:text-copper'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto pb-8 space-y-4">
                  <a 
                    href="tel:+19453584803" 
                    className="flex items-center space-x-2 text-white/80 hover:text-copper transition-colors"
                  >
                    <Phone size={18} />
                    <span className="font-inter">(945) 358-4803</span>
                  </a>
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    <Button 
                      className="w-full bg-copper hover:bg-copper/90 text-white py-3 text-sm font-bold uppercase tracking-widest"
                    >
                      Book Estimate
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
